import log from 'loglevel'
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material'
import {
  type Identifier,
  type RaRecord,
  type RowClickFunction,
  useTranslate,
  useUnselect,
  useUnselectAll
} from 'react-admin'
import React from 'react'
import {
  compact,
  concat,
  difference,
  filter,
  find,
  includes,
  isNil,
  map,
  size,
  template
} from 'lodash/fp'
import { SpotList } from 'components/SpotList'
import { isEmpty } from 'lodash'
import { type ConfigProps } from 'components/Dashboard'
import { getStep, useTourControl } from 'components/Step'
import { useTour } from '@reactour/tour'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

export const SpotSelect: React.FC<
  {
    rowClick?: RowClickFunction
    multiple?: boolean
    opened?: boolean
    onSelect?: (selectedIds: Identifier[], data: RaRecord[]) => void
    onSelectAll?:
      | ((selectedIds: Identifier[], data: RaRecord[]) => void)
      | undefined
    onSelectAllBy?:
      | ((selectedIds: Identifier[], data: RaRecord[], allBy: string) => void)
      | undefined
    onSelectAllManagedBy?:
      | ((
          selectedIds: Identifier[],
          data: RaRecord[],
          allManagedBy: string
        ) => void)
      | undefined
    tourKey?: string
    allBy?: string | null | undefined
    allManagedBy?: string | null | undefined
    error?: string
  } & ConfigProps
> = ({
  rowClick,
  multiple,
  opened,
  onSelect,
  onSelectAll,
  onSelectAllBy,
  onSelectAllManagedBy,
  config,
  allBy,
  allManagedBy,
  tourKey,
  error
}) => {
  const { setIsOpen } = useTourControl(tourKey ?? 'SpotSelect')
  const { setSteps, setCurrentStep } = useTour()
  const steps = getStep(tourKey ?? 'SpotSelect')
  const { SpotSelect: configSpotSelect } = config
  const translate = useTranslate()
  const [open, setOpen] = React.useState(!isNil(opened) && opened)
  const dialog = React.useMemo(() => {
    const handleClose = (): void => {
      setOpen(false)
    }
    const onClick = (): void => {
      setOpen(false)
    }
    const handleRowClick: RowClickFunction = async (
      id: Identifier,
      resource: string,
      record: RaRecord
    ) => {
      if (isNil(multiple) || !(multiple as boolean)) setOpen(false)
      // eslint-disable-next-line  @typescript-eslint/return-await
      if (!isNil(rowClick)) return await rowClick(id, resource, record)
      return false
    }
    return (
      <>
        <Dialog onClose={handleClose} open={open} maxWidth="lg" fullWidth>
          <DialogTitle id="spotSelectDialog">
            {configSpotSelect.dialog.title ??
              translate(`resources.spotSelect.dialog.title`)}
          </DialogTitle>
          <DialogContent>
            <Card>
              <CardContent>
                <SpotList
                  resource="mappings"
                  rowClick={handleRowClick}
                  multiple={multiple}
                  onSelect={onSelect}
                  onSelectAll={onSelectAll}
                  onSelectAllBy={onSelectAllBy}
                  onSelectAllManagedBy={onSelectAllManagedBy}
                  config={config}
                  setIsOpen={setIsOpen}
                  allBy={allBy}
                  allManagedBy={allManagedBy}
                />
                {!isNil(error) && !isEmpty(error) && (
                  <Typography color="red">{error}</Typography>
                )}
              </CardContent>
            </Card>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={onClick}>
              {translate('resources.spotSelect.buttons.close')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }, [open, rowClick, filter, multiple, opened])
  const onSelectClick = (): void => {
    setOpen(true)
  }
  React.useEffect(() => {
    if (isNil(steps) || isNil(setSteps)) return
    setSteps(steps)
    setCurrentStep(0)
  }, [])
  return (
    <>
      <Button variant="contained" onClick={onSelectClick} sx={{ m: 2 }}>
        {configSpotSelect.buttons.trigger ??
          translate('resources.spotSelect.buttons.trigger')}
      </Button>
      {dialog}
    </>
  )
}
export const MultipleSpotSelect: React.FC<
  {
    resource: string
    selected: RaRecord[]
    setSelected: (selected: RaRecord[]) => void
    deleted?: boolean | undefined
    setDeleted?: ((deleted: boolean) => void) | undefined
    noAll?: boolean | undefined
    allBy?: string
    allManagedBy?: string | null | undefined
    setAllBy?: (value: { key: string; value: string }) => void
    limit?: number
  } & ConfigProps
> = ({
  resource,
  selected,
  setSelected,
  deleted: deletedArg,
  setDeleted: setDeletedArg,
  noAll,
  allBy,
  allManagedBy,
  setAllBy,
  limit,
  config
}) => {
  const [deletedLocal, setDeletedLocal] = React.useState(false)
  const [error, setError] = React.useState<string>('')
  let deleted = deletedArg
  let setDeleted = setDeletedArg
  if (isNil(deletedArg)) {
    deleted = deletedLocal
    setDeleted = setDeletedLocal
  }
  const unselectAll = useUnselectAll(resource)
  const unselect = useUnselect(resource)
  const translate = useTranslate()
  const handleRowClick: RowClickFunction = (
    id: Identifier,
    _resource: string,
    record: RaRecord
  ): any => {
    // log.debug({ selected })
    if (isNil(find((item: RaRecord) => item.id === id)(selected))) {
      setSelected([...selected, record])
    }
    return 'toggleSelection'
  }
  const handleDelete = (deleted: RaRecord): void => {
    setSelected(filter((item: RaRecord) => item.id !== deleted.id)(selected))
  }
  const handleSelect = (selectedIds: Identifier[], data: RaRecord[]): void => {
    if (!isNil(limit)) {
      if (size(selectedIds) > limit) {
        setError(
          template(translate('resources.spotSelect.errors.limit'))({ limit })
        )
        return
      } else {
        setError('')
      }
    }
    // log.debug({ selectedIds, data, selected })
    if (isNil(selected)) return
    if (isEmpty(selected) && isEmpty(selectedIds)) {
      unselectAll()
      return
    }
    const current = map((item: RaRecord) => item.id)(selected)
    // log.debug({
    //   selected,
    //   selectedIds,
    //   diff1: difference(current)(selectedIds),
    //   diff2: difference(selectedIds)(current)
    // })
    for (const id of difference(current)(selectedIds)) {
      const record = find((item: RaRecord) => item.id === id)(data)
      if (!isNil(record)) handleDelete(record)
    }
    for (const id of difference(selectedIds)(current)) {
      const record = find((item: RaRecord) => item.id === id)(data)
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      if (!isNil(record)) handleRowClick(id, 'users', record)
    }
    if (!isNil(deleted) && (deleted as boolean)) {
      const diff = difference(selectedIds)(current)
      if (!isEmpty(diff)) unselect(diff)
      if (!isNil(setDeleted)) setDeleted(false)
    }
    const dataIds = map((item: RaRecord) => item.id)(data)
    if (isEmpty(selectedIds)) {
      setSelected([])
    } else {
      setSelected(
        concat(
          compact(
            map((item: RaRecord) =>
              includes(item.id)(dataIds) && !includes(item.id)(selectedIds)
                ? undefined
                : item
            )(selected)
          )
        )(
          compact(
            map((item: RaRecord) =>
              includes(item.id)(selectedIds) && !includes(item.id)(current)
                ? item
                : undefined
            )(data)
          )
        )
      )
    }
  }
  const handleSelectAll = (
    _selectedIds: Identifier[],
    data: RaRecord[]
  ): void => {
    if (!isEmpty(data) && !isNil(noAll) && (noAll as boolean)) return
    setSelected(data)
  }
  const handleSelectAllBy = (
    _selectedIds: Identifier[],
    data: RaRecord[],
    allByValue: string
  ): void => {
    if (!isNil(setAllBy) && !isNil(allBy)) {
      setAllBy({ key: allBy, value: allByValue })
    }
    setSelected(data)
  }
  const handleSelectAllManagedBy = (
    _selectedIds: Identifier[],
    data: RaRecord[],
    allManagedByValue: string
  ): void => {
    if (!isNil(setAllBy) && !isNil(allManagedBy)) {
      setAllBy({ key: allManagedBy, value: allManagedByValue })
    }
    setSelected(data)
  }
  React.useLayoutEffect(() => {
    unselectAll()
  }, [])
  return (
    <SpotSelect
      rowClick={handleRowClick}
      opened={isEmpty(selected)}
      multiple
      onSelect={handleSelect}
      onSelectAll={handleSelectAll}
      onSelectAllBy={isNil(allBy) ? undefined : handleSelectAllBy}
      onSelectAllManagedBy={isNil(allBy) ? undefined : handleSelectAllManagedBy}
      config={config}
      tourKey="MultipleSpotSelect"
      allBy={allBy}
      allManagedBy={allManagedBy}
      error={error}
    />
  )
}
